import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@noor/ui';

/**
 * The attachment viewer — the read-only path of the old `ImageUploadGallery`:
 * the thread's images in chronological order with prev/next stepping and a
 * position counter. Renders nothing while closed.
 */
export function MerchantAttachmentLightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  /** Thread image URLs, oldest first. */
  images: string[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const open = images.length > 0;
  const current = images[Math.min(index, images.length - 1)];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        closeOnInteractOutside
        className="max-h-[calc(100dvh-4rem)] max-w-3xl gap-2 overflow-hidden p-2"
      >
        <DialogTitle className="sr-only">{t('chat.messages')}</DialogTitle>
        {current && (
          <img
            src={current}
            alt=""
            className="max-h-[calc(100dvh-10rem)] w-full rounded-lg object-contain"
          />
        )}
        {images.length > 1 && (
          <div className="flex items-center justify-between px-2 py-1">
            <button
              type="button"
              onClick={() => onIndexChange((index - 1 + images.length) % images.length)}
              aria-label={t('actions.back')}
              className="cursor-pointer rounded-full p-2 transition-colors outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <ChevronLeftIcon className="size-5" />
            </button>
            <span className="text-sm font-medium text-muted-foreground tabular-nums">
              {Math.min(index, images.length - 1) + 1} / {images.length}
            </span>
            <button
              type="button"
              onClick={() => onIndexChange((index + 1) % images.length)}
              aria-label={t('actions.next')}
              className="cursor-pointer rounded-full p-2 transition-colors outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <ChevronRightIcon className="size-5" />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
